import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Save, RefreshCw, Eye } from 'lucide-react';
import { toast } from 'sonner';
import ImageUploadField from './ImageUploadField';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  is_active: true,
  display_type: 'image_text',
  ribbon_tr: '',
  ribbon_en: '',
  title_tr: '',
  title_en: '',
  subtitle_tr: '',
  subtitle_en: '',
  description_tr: '',
  description_en: '',
  old_price: '',
  new_price: '',
  image: '',
  limited_tr: '',
  limited_en: '',
};

const CampaignPanel = ({ token }) => {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/campaign`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('fetch failed');
      const json = await res.json();
      setData({ ...EMPTY, ...json });
    } catch {
      toast.error('Kampanya bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/campaign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('save failed');
      const json = await res.json();
      setData({ ...EMPTY, ...json });
      toast.success('Kampanya güncellendi');
    } catch {
      toast.error('Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const patch = (patchObj) => setData((d) => ({ ...d, ...patchObj }));

  const previewCampaign = () => {
    // Clear sessionStorage flag so popup re-appears on next visit
    try { sessionStorage.removeItem('campaign-shown'); } catch {}
    window.open('/?preview=campaign', '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <div className="text-center py-12 text-muted-foreground">Yükleniyor…</div>;
  }

  const imageOnly = data.display_type === 'image_only';
  const dim = imageOnly ? 'opacity-50 pointer-events-none select-none' : '';

  return (
    <div className="space-y-4" data-testid="campaign-panel">
      {/* Header row */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3">
            <Switch
              checked={data.is_active}
              onCheckedChange={(v) => patch({ is_active: v })}
              data-testid="campaign-active-switch"
            />
            <span className="text-sm">
              {data.is_active ? 'Popup aktif (kullanıcılar görüyor)' : 'Popup kapalı (gizli)'}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={previewCampaign} data-testid="campaign-preview-btn">
              <Eye className="w-4 h-4 mr-2" />Önizle
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />Yenile
            </Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="campaign-save-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Kaydediliyor…' : 'Kaydet'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Gösterim Tipi */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <label className="text-sm font-medium text-foreground">Gösterim Tipi</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => patch({ display_type: 'image_only' })}
              data-testid="campaign-display-image-only"
              className={`text-left rounded-lg border p-3 transition-all ${imageOnly ? 'border-primary bg-primary/10' : 'border-border/50 hover:border-primary/40'}`}
            >
              <div className="font-medium text-sm mb-1">Sadece Görsel</div>
              <div className="text-xs text-muted-foreground">Popup'ta yalnızca kampanya görseli ve kapatma butonu gösterilir.</div>
            </button>
            <button
              type="button"
              onClick={() => patch({ display_type: 'image_text' })}
              data-testid="campaign-display-image-text"
              className={`text-left rounded-lg border p-3 transition-all ${!imageOnly ? 'border-primary bg-primary/10' : 'border-border/50 hover:border-primary/40'}`}
            >
              <div className="font-medium text-sm mb-1">Görsel + Metin</div>
              <div className="text-xs text-muted-foreground">Rozet, başlık, açıklama ve fiyat bilgileri birlikte gösterilir.</div>
            </button>
          </div>
          {imageOnly && (
            <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-3">
              <strong>Not:</strong> "Sadece Görsel" seçili olduğundan metin alanları devre dışı. Metin bilgileri saklanmaya devam eder — gösterim tipini değiştirince tekrar aktif olur.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Görsel */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <label className="text-sm font-medium text-foreground">Kampanya Görseli</label>
          <ImageUploadField
            value={data.image}
            onChange={(url) => patch({ image: url })}
            testIdPrefix="campaign-image"
          />
        </CardContent>
      </Card>

      {/* Rozet */}
      <Card className={dim}>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-muted-foreground">Rozet Yazısı (TR)</label>
            <Input
              value={data.ribbon_tr}
              onChange={(e) => patch({ ribbon_tr: e.target.value })}
              placeholder="GÜNÜN ÜRÜNÜ"
              data-testid="campaign-ribbon-tr"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Rozet Yazısı (EN)</label>
            <Input
              value={data.ribbon_en}
              onChange={(e) => patch({ ribbon_en: e.target.value })}
              placeholder="PRODUCT OF THE DAY"
            />
          </div>
        </CardContent>
      </Card>

      {/* Başlık ve alt başlık */}
      <Card className={dim}>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-muted-foreground">Başlık (TR)</label>
            <Input
              value={data.title_tr}
              onChange={(e) => patch({ title_tr: e.target.value })}
              placeholder="PEYNİRLİ PİDE + AYRAN"
              data-testid="campaign-title-tr"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Başlık (EN)</label>
            <Input
              value={data.title_en}
              onChange={(e) => patch({ title_en: e.target.value })}
              placeholder="CHEESE PIDE + AYRAN"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Alt Başlık (TR)</label>
            <Input
              value={data.subtitle_tr}
              onChange={(e) => patch({ subtitle_tr: e.target.value })}
              placeholder="Eşsiz İkili"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Alt Başlık (EN)</label>
            <Input
              value={data.subtitle_en}
              onChange={(e) => patch({ subtitle_en: e.target.value })}
              placeholder="Perfect Duo"
            />
          </div>
        </CardContent>
      </Card>

      {/* Açıklama */}
      <Card className={dim}>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-muted-foreground">Açıklama (TR)</label>
            <Textarea
              rows={3}
              value={data.description_tr}
              onChange={(e) => patch({ description_tr: e.target.value })}
              placeholder="Sıcacık peynirli pide yanında soğuk ayranla…"
              data-testid="campaign-desc-tr"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Açıklama (EN)</label>
            <Textarea
              rows={3}
              value={data.description_en}
              onChange={(e) => patch({ description_en: e.target.value })}
              placeholder="Hot cheese pide served with cold ayran…"
            />
          </div>
        </CardContent>
      </Card>

      {/* Fiyatlar */}
      <Card className={dim}>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-muted-foreground">Eski Fiyat (üstü çizili)</label>
            <Input
              value={data.old_price}
              onChange={(e) => patch({ old_price: e.target.value })}
              placeholder="₺650"
              data-testid="campaign-old-price"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Yeni Fiyat</label>
            <Input
              value={data.new_price}
              onChange={(e) => patch({ new_price: e.target.value })}
              placeholder="₺600"
              data-testid="campaign-new-price"
            />
          </div>
        </CardContent>
      </Card>

      {/* Limited etiket */}
      <Card className={dim}>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-muted-foreground">Alt Uyarı (TR)</label>
            <Input
              value={data.limited_tr}
              onChange={(e) => patch({ limited_tr: e.target.value })}
              placeholder="Sınırlı süre!"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Alt Uyarı (EN)</label>
            <Input
              value={data.limited_en}
              onChange={(e) => patch({ limited_en: e.target.value })}
              placeholder="Limited time!"
            />
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground text-center pb-4">
        Değişiklikler kaydedildikten sonra kullanıcılar bir sonraki oturumda güncel banner'ı görür.
      </div>
    </div>
  );
};

export default CampaignPanel;
